import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { BellIcon } from '@heroicons/react/solid'

// How loud one DM thread is, after Closing Credits' chat rooms. Mirrors
// PostUser::NOTIFY_LEVELS; PostMessage#audience is what each one actually
// lets through, so keep these descriptions true to it.
export const NOTIFY_LEVELS = [
  { value: 'all', label: 'All activity', description: 'Every new message, plus mentions' },
  { value: 'mentions', label: 'Mentions only', description: 'Only when someone @mentions you' },
  { value: 'none', label: 'Nothing', description: 'No notifications or emails from this thread' }
]

export const BellOffIcon = ({ className }) => (
  <svg viewBox='0 0 20 20' fill='currentColor' className={className} aria-hidden='true'>
    <path d='M13.28 3.22a.75.75 0 10-1.06 1.06l1.372 1.373A5.98 5.98 0 0114 8v2.28l1.5 1.5V8a7.47 7.47 0 00-1.06-3.84l-1.16-.94zM4 8a6 6 0 018.9-5.24L3.22 12.44A5.97 5.97 0 014 10V8zm-1.78-4.78a.75.75 0 011.06 0l13.5 13.5a.75.75 0 11-1.06 1.06L2.22 4.28a.75.75 0 010-1.06zM8 17a2 2 0 003.86.73L8 14.87V17z' />
  </svg>
)

const NotifyLevelMenu = ({ level = 'all', onChange }) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const current = NOTIFY_LEVELS.find(l => l.value === level) || NOTIFY_LEVELS[0]
  const silenced = level === 'none'
  const Icon = silenced ? BellOffIcon : BellIcon

  const pick = (value) => {
    setOpen(false)
    if (value !== level) onChange(value)
  }

  return <div className='relative shrink-0' ref={wrapRef}>
    <button
      type='button'
      onClick={() => setOpen(o => !o)}
      aria-haspopup='menu'
      aria-expanded={open}
      aria-label={`Notifications: ${current.label}`}
      title={`Notifications: ${current.label}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${
        silenced
          ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
      }`}
    >
      <Icon className='h-4 w-4' />
      <span className='hidden sm:inline'>{current.label}</span>
    </button>

    {open && <div role='menu' className='absolute right-0 z-40 mt-1 w-64 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl p-1'>
      <div className='px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400'>Notify me about</div>
      {NOTIFY_LEVELS.map(option => (
        <button
          key={option.value}
          type='button'
          role='menuitemradio'
          aria-checked={option.value === level}
          onClick={() => pick(option.value)}
          className={`w-full text-left px-3 py-2 rounded-lg ${option.value === level ? 'bg-cccblue/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
        >
          <span className={`block text-sm font-semibold ${option.value === level ? 'text-cccblue' : 'text-gray-900 dark:text-white'}`}>{option.label}</span>
          <span className='block text-xs text-gray-500 dark:text-gray-400'>{option.description}</span>
        </button>
      ))}
    </div>}
  </div>
}

export default NotifyLevelMenu

BellOffIcon.propTypes = { className: PropTypes.string }
NotifyLevelMenu.propTypes = { level: PropTypes.string, onChange: PropTypes.func.isRequired }
